import { useMemo, useState } from 'react';
import { MapboxProfile } from '@mapbox/mapbox-sdk/lib/classes/mapi-request';
import { generate } from '../../modules/instruction-generation';
import { downloadButtonClickEvent, profileChangeEvent } from '../../modules/analytics';
import { openURL } from '../../modules/messaging';

// Components
import Tabs from '../../components/tabs';
import ErrorFallback from '../../components/error-fallback';

// Containers
import Stops from '../stops';
import Stats from '../stats';

// Contexts
import { useControlls } from '../../contexts/controlls.context';
import { useStats } from '../../contexts/stats.context';

// Utils
import { metersToMiles, formatDistance } from '../../utils/distance';
import { secondsToHours, secondsToMinutes } from '../../utils/time';
import { removeFromLastChar } from '../../utils/string';
import { snapshotEffect } from '../../utils/snapshot-effect';
import { sleep } from '../../utils/utils';

// Contexts
import { useNavigation } from '../../contexts/navigation.context';
import { useErrorBoundary } from '../../contexts/error-boundary.context';

// Interfaces
import { InstructionStep } from '../../interfaces';

import './controlls.scss';

const SECONDS_IN_HOUR = 60 * 60;

interface ControllsProps {}

const Controlls: React.FunctionComponent<ControllsProps> = () => {
	const { profile, setProfile, stops: steps, setStops } = useControlls();
	const { map, steps: navigationSteps } = useNavigation();
	const { time, stops, distance } = useStats();
	const { isError } = useErrorBoundary();
	const [isGenerating, setIsGenerating] = useState(false);
	const isTimeInHours = time > SECONDS_IN_HOUR;

	const formatedTime = useMemo(
		() => ({
			label: isTimeInHours ? 'hours' : 'minutes',
			time: formatTime(isTimeInHours ? secondsToHours(time) : Math.round(secondsToMinutes(time))).toString()
		}),
		[isTimeInHours, time]
	);

	const formatedDistance = useMemo(() => formatDistance(metersToMiles(distance)), [distance]);
	const formatedSteps = useMemo(
		() =>
			navigationSteps?.map<InstructionStep>(step => ({
				...step,
				distance: metersToMiles(step.distance)
			})),
		[navigationSteps]
	);

	function formatTime(time: number) {
		if (time.toString().substr(time.toString().lastIndexOf('.')).length > 1) return time.toFixed(1);
		else return time;
	}

	function onProfileChange(profile: MapboxProfile) {
		setProfile(profile);
		profileChangeEvent({ eventValue: profile });
	}

	async function onDownload() {
		if (formatedSteps?.length) {
			setIsGenerating(true);

			const execute = async () => {
				snapshotEffect();

				await sleep(10);

				map?.current?.setPadding({ left: 60, right: 60, top: 60, bottom: 60 });

				const mapCanvas = map?.current?.getCanvas();

				await sleep(20);

				setTimeout(() => {
					map?.current?.setPadding({ left: 60, right: 320, top: 60, bottom: 60 });
				}, 10);

				const pdf = await generate({
					mapCanvas,
					distance: formatedDistance,
					duration: formatedTime,
					logo: require('../../assets/logo-pdf.svg').default,
					steps: formatedSteps!,
					origin: removeFromLastChar(steps[0].label, ','),
					destination: removeFromLastChar(steps[steps.length - 1].label, ',')
				});

				openURL(pdf);
				setIsGenerating(false);
				downloadButtonClickEvent();
			};

			if (map?.current?.isZooming()) {
				map?.current?.once('zoomend', () => {
					execute();
				});
			} else {
				execute();
			}
		}
	}

	return (
		<div className="controlls">
			{!isError ? (
				<>
					<Tabs value={profile} onChange={onProfileChange}>
						<Tabs.Tab value={'cycling'}>Cycling</Tabs.Tab>
						<Tabs.Tab value={'driving'}>Driving</Tabs.Tab>
						<Tabs.Tab value={'walking'}>Walking</Tabs.Tab>
					</Tabs>
					<Stops value={steps} style={{ marginTop: '4px' }} onChange={setStops} />
					<Stats
						stats={[
							{
								label: formatedTime.label,
								value: formatedTime.time
							},
							{ label: 'stops', value: stops.toString() },
							{ label: 'miles', value: formatedDistance }
						]}
						style={{ marginTop: 'auto' }}
						isGenerating={isGenerating}
						onDownload={onDownload}
					/>
				</>
			) : (
				<ErrorFallback />
			)}
		</div>
	);
};

export default Controlls;
